import React, { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { AuthContext } from 'context/AuthProvider';
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import Avatar from 'components/UI/Antd/Avatar/Avatar';
import pickAPierLogo from 'assets/images/pickapier/logo_circle.svg';
import firebaseService from 'services/firebaseService.js';

import {
  getStatus,
  formatPrice,
} from 'library/helpers/utils';

import {
  DateString,
  MessagesDivider,
  AvatarWrapper,
  StatusWrapper,
  MessageWrapper,
  MessageContent,
  MessageHeader,
  MessageTitle,
  MessageTime,
  MessageText
} from './Messaging.style';
import {formatLocalizedDate, formatLocalizedDateRange} from "../../library/helpers/dates";

const Message = ({ message, marina }) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { sender, text, attachment } = message;

  if (!user) {
    return null;
  }

  const getAvatar = () => {
    let title = '';
    if (sender === 'MARINA') {
      const ws = marina.name ? marina.name.split(' ') : 'Marina';
      if (ws.length <= 1) {
        title = marina.name.substring(0, 2).toUpperCase();
      } else {
        title = `${ws[0].charAt(0).toUpperCase()}${ws[1]
          .charAt(0)
          .toUpperCase()}`;
      }
    } else if (sender === 'BOATER') {
      const { first_name, last_name } = user;
      if (first_name && last_name) {
        title = `${user.first_name.charAt(0).toUpperCase()}${user.last_name
          .charAt(0)
          .toUpperCase()}`;
      } else {
        // ToDo: provide the correct default name in the next version
        title = 'N/A';
      }
    } else {
      title = null;
    }
    return title;
  };

  let backgroundColor = 'transparent';
  let messageTitle = t('PICKAPIER_TITLE');
  let src = null;
  let shape = 'circle';
  let className = '';
  if (sender === 'MARINA') {
    backgroundColor = '#1f253e';
    messageTitle = marina.name;
  } else if (sender === 'BOATER') {
    backgroundColor = '#66bdff';
    messageTitle = t('YOU');
  } else {
    src = pickAPierLogo;
    shape = 'square';
    className = 'contained';
  }

  const renderMessage = (text) => {
    if (typeof text === 'string') {
      return text
        .trim()
        .split('\n')
        .map(t => (
          <>
            {t}
            <br />
          </>
        ));
    } else {
      return text;
    }
  };

  const onDownloadAttachment = async () => {
    const url = await firebaseService.getFileUrl(attachment.file_path);

    if (url) {
      let xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.onload = function () {
          if (xhr.status && xhr.status === 200) {
            var url = window.URL.createObjectURL(xhr.response);
            var a = document.createElement('a');
            a.href = url;
            a.download = attachment.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
      };
      xhr.send();
    }
  }

  return (
    <MessageWrapper>
      <AvatarWrapper>
        <Avatar
          className={className}
          src={src}
          shape={shape}
          size='large'
          style={{
            fontSize: '16px',
            color: '#ffffff',
            backgroundColor
          }}
        >
          {getAvatar()}
        </Avatar>
      </AvatarWrapper>
      <MessageContent>
        <MessageHeader>
          <MessageTitle>{messageTitle}</MessageTitle>
          {(sender === 'MARINA' || sender === 'BOATER') && (
            <MessageTime>
              {moment(message.created_at).format('HH:mm')}
            </MessageTime>
          )}
        </MessageHeader>
        <MessageText>
          {renderMessage(text)}
          {attachment? <span className='link' onClick={ onDownloadAttachment }> {t('DOWNLOAD')} </span> : <></>}
        </MessageText>
      </MessageContent>
    </MessageWrapper>
  );
};

const Status = ({ status, marina, isMissingRequirements, reservation }) => {
  const { t } = useTranslation();
  const { name } = marina;
  const { status_code, action_required } = status;
  let title = '';
  let type = '';

  const marinaLogos = (marina_id) => {
    return ((process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina_id === 82)
    || (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina_id === 411))
  }

  const marinaPuertoSherry = (marina_id) => {
    return ((process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina_id === 98)
    || (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina_id === 352))
    ||  (process.env.REACT_APP_FIREBASE_ENV === 'DEVELOPMENT' && marina_id === 7)
  }

  const getPrice = () => {
    if(reservation.payment.current_pricing_plan?.deposit) return formatPrice(reservation.payment.current_pricing_plan?.deposit, 'en', marina.marina_settings.currency);
    let total = reservation.payment.deposit_rate? 
        (parseFloat(reservation.payment.total_to_pay) * (reservation.payment.deposit_rate / 100).toFixed(2) ) 
        :
        reservation.payment.total_to_pay;
    if (reservation.payment && reservation.payment.popeye_discount) {
      const { payment } = reservation;
      const { popeye_discount } = payment;
      total = isEmpty(popeye_discount) ? total : total - popeye_discount.total_money_saved;
    }
    return formatPrice(total, 'en', marina.marina_settings.currency);
  };

  switch (status_code) {
    case 'INITIATED':
      type = 'INFO';
      title = t('REQUEST_SENT_TO_MARINA_WAIT', { name });
      break;
    case 'UPDATED':
      type = 'INFO';
      if (isMissingRequirements) {
        title = t('MARINA_SENT_YOU_PRICE_FILL_FIELDS', { name });
      } else {
        title = t('MARINA_SENT_YOU_PRICE_GOOD_TO_GO', { name });
      }
      break;
    case 'BOATER_ADDED_DOCUMENTS':
      type = 'INFO';
      title = t('DOCUMENTS_SENT_TO_MARINA_WAIT', { name });
      break;
    case 'MARINA_ADDED_DATES':
      type = 'INFO';
      title = t('MARINA_SUGGESTED_NEW_DATES', status && status.suggested_dates && status.suggested_dates.length > 0
          ? { dates: formatLocalizedDateRange(status.suggested_dates[0].start_date, status.suggested_dates[0].end_date)}
          : {dates: 'n/a - n/a'});
      break;
    case 'APPROVED':
      type = 'APPROVED';
      title = t('MARINA_APPROVED_REQUEST', { name });
      break;
    case 'CANCELED_BY_MARINA':
      type = 'CANCELED';
      title = t('MARINA_CANCELED_REQUEST', { name });
      break;
    case 'CANCELED_BY_BOATER':
      type = 'CANCELED';
      title = t('YOU_CANCELED_REQUEST');
      break;
    case 'BOATER_ACCEPTED_SUGGESTED_DATES':
      type = 'INFO';
      title = t('YOU_ACCEPTED_SUGGESTED_DATES', {
        dates: formatLocalizedDateRange(status?.suggested_dates[0]?.start_date, status?.suggested_dates[0]?.end_date)});
      break;
    case 'INSTANT_BOOK':
      type = 'APPROVED';
      title = t('MARINA_APPROVED_REQUEST', { name });
      break;
    case 'WAITING_LIST':
      type = 'WAITING_LIST';
      title = t('WAITING_LIST_CHAT', { Marina_name: name });
      break;
    case 'EXPIRED_NO_BOATER_RESPONSE':
      type = 'CANCELED';
      title = t('BOATER_EXPIRED_REQUEST');
      break;
    case 'EXPIRED_NO_MARINA_RESPONSE':
      type = 'CANCELED';
      title = t('MARINA_EXPIRED_REQUEST');
      break;
    default:
      type = 'INFO';
      break;
  }

  const getIcon = () => {
    const icons = {
      INFO: <InfoCircleFilled className='status_icon' />,
      WARNING: <ExclamationCircleFilled className='status_icon' />,
      CANCELED: <CloseCircleFilled className='status_icon' />,
      APPROVED: <CheckCircleFilled className='status_icon' />
    };
    return icons[type] || icons['INFO'];
  };

  const depositPaid = reservation?.pricing_plan?.deposit && reservation?.pricing_plan?.deposit !== reservation?.pricing_plan?.total_to_pay;

  return (
    <>
      {status_code === 'APPROVED' && reservation.payment.paid_at && (
        <MessageWrapper>
          <StatusWrapper status={type}>{getIcon()}</StatusWrapper>
          <MessageContent>
            <MessageHeader>
              <MessageTitle>
                {t(depositPaid ? 'PAYMENT_DEPOSIT_APPROVED_INDICATION' : 'PAYMENT_APPROVED_INDICATION', {
                  payment_amount: `${getPrice()}`
                })}
              </MessageTitle>
              <MessageTime>
                {moment(reservation.payment.paid_at).format('HH:mm')}
              </MessageTime>
            </MessageHeader>
          </MessageContent>
        </MessageWrapper>
      )}
      <MessageWrapper>
        <StatusWrapper status={type}>{getIcon()}</StatusWrapper>
        <MessageContent>
          <MessageHeader>
            <MessageTitle>{title}</MessageTitle>
            <MessageTime>
              {moment(status.created_at).format('HH:mm')}
            </MessageTime>
          </MessageHeader>
        </MessageContent>
      </MessageWrapper>
      { reservation.payment_accepted_online &&
        reservation.payment &&
        reservation.payment.total_to_pay &&
        reservation.payment.paid_at &&
        (status_code === 'CANCELED_BY_BOATER' ||
          status_code === 'CANCELED_BY_MARINA') && (
          <MessageWrapper>
            <StatusWrapper status={type}>{getIcon()}</StatusWrapper>
            <MessageContent>
              <MessageHeader>
                <MessageTitle>
                  {t('PAYMENT_REFUND_LATER')}
                </MessageTitle>
                <MessageTime>
                  {moment(reservation.payment.paid_at).format('HH:mm')}
                </MessageTime>
              </MessageHeader>
            </MessageContent>
          </MessageWrapper>
        )}
    </>
  );
};

const MessagesList = ({
  isAllowed,
  isReservation = false,
  messagesAndStatuses,
  read,
  unread,
  marina,
  isMissingRequirements,
  reservation
}) => {
  const { t } = useTranslation();
  const onSupportClick = e => {
    e.preventDefault();
    window.zE && window.zE.activate();
  };

  const getNotAllowedMessage = () => {
    return (
      <Fragment>
        <div>{t('CURRENTLY_MARINA_UNABLE_TO_RECEIVE_MESSAGES')}</div>
        <div>
          {t('FOR_ANY_QUESTIONS_CONTACT_SUPPORT')}{' '}
          <a href='#' onClick={onSupportClick} className='link'>
            {t('SUPPORT_LINK')}
          </a>
          .
        </div>
      </Fragment>
    );
  };

  if (
    isReservation &&
    !isAllowed &&
    !read.filter(r => r.id === 'message_id').length
  ) {
    read.splice(1, 0, {
      receiver: 'BOATER',
      text: getNotAllowedMessage(),
      created_at: read[0] ? read[0].created_at : moment(),
      id: 'message_id'
    });
  }

  const getDateDivider = (message, index) => {
    if (index === 0) {
      return (
        <DateString>{formatLocalizedDate(message.created_at)}</DateString>
      );
    } else {
      const prevDate = moment(read[index - 1].created_at).startOf('day');
      const nextDate = moment(message.created_at).startOf('day');

      if (nextDate.diff(prevDate, 'days') >= 1) {
        return (
          <DateString>
            {formatLocalizedDate(message.created_at, true)}
          </DateString>
        );
      }
      return null;
    }
    return null;
  };

  const getReadMessages = () => {
    if (!read.length) {
      return null;
    }
    return read.map((message, index) => {
      const isMessage = !!message.text;
      return (
        <div key={message.id}>
          {getDateDivider(message, index)}
          {isMessage ? (
            <Message message={message} marina={marina} />
          ) : (
            <Status
              reservation={reservation}
              status={message}
              marina={marina}
              isMissingRequirements={isMissingRequirements}
            />
          )}
        </div>
      );
    });
  };

  const getUnreadMessages = () => {
    if (!unread.length) {
      return null;
    }
    return unread.map((message, index) => {
      const isMessage = !!message.text;
      return (
        <div key={message.id}>
          {isMessage ? (
            <Message message={message} marina={marina} />
          ) : (
            <Status
              reservation={reservation}
              status={message}
              marina={marina}
              isMissingRequirements={isMissingRequirements}
            />
          )}
        </div>
      );
    });
  };

  const getServiceMessage = () => {
    return (
      <Fragment>
        <div>
          {t('THIS_IS_BEGINNING_CONVERSATION_WITH_MARINA', {
            marina_name: marina.name
          })}{' '}
          {t('FEEL_FREE_TO_WRITE_QUESTIONS')}
        </div>
        <div>
          {t('NO_ANSWER_CONTACT_SUPPORT')}{' '}
          <a href='#' onClick={onSupportClick} className='link'>
            {t('SUPPORT_TEAM')}
          </a>
          .
        </div>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {isAllowed ? (
        <div>
          <Message message={{ text: getServiceMessage() }} />
        </div>
      ) : (
        <Fragment>
          {!isReservation && (
            <div>
              <Message message={{ text: getNotAllowedMessage() }} />
            </div>
          )}
        </Fragment>
      )}
      {!!(messagesAndStatuses && messagesAndStatuses.length) && (
        <Fragment>
          {getReadMessages()}
          {!!(unread && unread.length) && (
            <Fragment>
              <MessagesDivider>{`- ${t('NEW_UNREAD_MESSAGES', {
                number: unread.length
              })} -`}</MessagesDivider>
              {getUnreadMessages()}
            </Fragment>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default MessagesList;
